import type { StyleParams, EmotionLabel } from '../types';

const DEFAULT_STYLE: StyleParams = {
  rotationSpeed: 0.01,
  trailLength: 12,
  sizeVariance: 0.2,
  spawnSpread: 0.5,
  glowMultiplier: 1.0,
  particleBurstCount: 4,
  shapeScale: 1.0,
  alphaMultiplier: 1.0,
};

const EMOTION_STYLES: Record<EmotionLabel, StyleParams> = {
  happy: {
    rotationSpeed: 0.02,
    trailLength: 12,
    sizeVariance: 0.3,
    spawnSpread: 0.6,
    glowMultiplier: 1.4,
    particleBurstCount: 6,
    shapeScale: 1.1,
    alphaMultiplier: 1.0,
  },
  sad: {
    rotationSpeed: 0.003,
    trailLength: 20,
    sizeVariance: 0.1,
    spawnSpread: 0.35,
    glowMultiplier: 0.7,
    particleBurstCount: 2,
    shapeScale: 0.85,
    alphaMultiplier: 0.8,
  },
  angry: {
    rotationSpeed: 0.06,
    trailLength: 6,
    sizeVariance: 0.5,
    spawnSpread: 0.7,
    glowMultiplier: 1.8,
    particleBurstCount: 10,
    shapeScale: 1.3,
    alphaMultiplier: 1.0,
  },
  fear: {
    rotationSpeed: 0.04,
    trailLength: 24,
    sizeVariance: 0.4,
    spawnSpread: 0.8,
    glowMultiplier: 1.0,
    particleBurstCount: 4,
    shapeScale: 0.9,
    alphaMultiplier: 0.7,
  },
  neutral: {
    rotationSpeed: 0.01,
    trailLength: 12,
    sizeVariance: 0.2,
    spawnSpread: 0.5,
    glowMultiplier: 1.0,
    particleBurstCount: 4,
    shapeScale: 1.0,
    alphaMultiplier: 1.0,
  },
  surprise: {
    rotationSpeed: 0.05,
    trailLength: 8,
    sizeVariance: 0.7,
    spawnSpread: 0.9,
    glowMultiplier: 1.6,
    particleBurstCount: 12,
    shapeScale: 1.4,
    alphaMultiplier: 1.0,
  },
  disgust: {
    rotationSpeed: 0.008,
    trailLength: 16,
    sizeVariance: 0.3,
    spawnSpread: 0.4,
    glowMultiplier: 0.8,
    particleBurstCount: 3,
    shapeScale: 0.8,
    alphaMultiplier: 0.75,
  },
};

export function getStyleForEmotion(emotion: string | undefined): StyleParams {
  if (!emotion) return DEFAULT_STYLE;
  return EMOTION_STYLES[emotion as EmotionLabel] ?? DEFAULT_STYLE;
}

export function interpolateStyles(a: StyleParams, b: StyleParams, t: number): StyleParams {
  const lerp = (x: number, y: number) => x + (y - x) * t;
  return {
    rotationSpeed: lerp(a.rotationSpeed, b.rotationSpeed),
    trailLength: Math.round(lerp(a.trailLength, b.trailLength)),
    sizeVariance: lerp(a.sizeVariance, b.sizeVariance),
    spawnSpread: lerp(a.spawnSpread, b.spawnSpread),
    glowMultiplier: lerp(a.glowMultiplier, b.glowMultiplier),
    particleBurstCount: Math.round(lerp(a.particleBurstCount, b.particleBurstCount)),
    shapeScale: lerp(a.shapeScale, b.shapeScale),
    alphaMultiplier: lerp(a.alphaMultiplier, b.alphaMultiplier),
  };
}
